import React from "react";
import {RuleGroupType} from "react-querybuilder";
import * as styles from "./ReadOnlyQueryView.module.css";
import VulSource from "../../domain/VulSource";
import {Link} from "react-router-dom";
import AiAttribute from "../../domain/AiAttribute";
import {isValuelessAttributeOperator} from "../../utils/RqbOperatorUtils";

interface Props {
    sources?: VulSource[];
    aiAttributes?: AiAttribute[];
    query: RuleGroupType;
}

type QueryGroupNode = RuleGroupType & {
    not?: boolean;
};

type QueryRuleNode = {
    id?: string | number;
    field?: string;
    operator?: string;
    value?: unknown;
    not?: boolean;
};

type QueryNode = QueryGroupNode | QueryRuleNode;

type RuleDescriptor = {
    kind: string;
    subject: string;
    forceKindLabel: boolean;
    hideValue?: boolean;
    customValue?: React.ReactNode;
};

function ReadOnlyQueryView(props: Props) {
    const rootIsGroup = isGroupNode(props.query);

    return (
        <div className={`${styles.queryView} ${rootIsGroup ? styles.detailGroupRoot : styles.detail}`}>
            {renderNode(props.query, 0)}
        </div>
    );

    function formatAiAttributeId(aiAttributeId: string) {
        const aiAttribute = props.aiAttributes?.find(item => item.id === aiAttributeId);

        return aiAttribute?.name ?? aiAttributeId;
    }

    function renderNode(node: QueryNode, depth: number) {
        if (isGroupNode(node)) {
            return renderGroup(node, depth);
        }

        return renderRule(node, depth);
    }

    function renderGroup(node: QueryGroupNode, depth: number) {
        const rules = Array.isArray(node.rules) ? node.rules as QueryNode[] : [];

        return (
            <section
                className={`${styles.group} ${styles.groupDetail}`}
                data-depth={depth}
            >
                <div className={styles.groupHeader}>
                    <div className={styles.groupHeaderTop}>
                        <span className={`${styles.combinatorBadge} ${node.combinator === "and" ? styles.combinatorAnd : styles.combinatorOr}`}>
                            {formatCombinatorLabel(node.combinator)}
                        </span>
                        {node.not === true && <span className={styles.negationBadge}>NOT</span>}
                        <span className={styles.groupDescription}>
                            {formatCombinatorDescription(node.combinator)}
                        </span>
                    </div>
                </div>

                <div className={styles.groupBody}>
                    {rules.map((child, index: number) => (
                        <div key={child?.id || `${depth}-${index}`} className={styles.branch}>
                            <div className={`${styles.branchLabel} ${isGroupNode(child) ? styles.branchLabelGroup : styles.branchLabelRule}`}>
                                {formatBranchLabel(index)}
                            </div>

                            <div className={styles.branchContent}>
                                {renderNode(child, depth + 1)}
                            </div>
                        </div>
                    ))}
                </div>
            </section>
        );
    }

    function renderRule(rule: QueryRuleNode, depth: number) {
        const descriptor = describeRule(rule);
        const operator = formatOperator(rule?.operator);
        const value = descriptor.hideValue
            ? null
            : descriptor.customValue ?? renderValue(rule?.value, rule);

        return (
            <div
                className={`${styles.ruleRow} ${styles.ruleRowDetail}`}
                data-depth={depth}
            >
                <div className={styles.ruleContent}>
                    {rule?.not === true && <span className={styles.negationBadge}>NOT</span>}
                    {shouldRenderKindLabel(descriptor) && <span className={styles.kindLabel}>{descriptor.kind}</span>}
                    <span className={styles.subject}>{descriptor.subject}</span>
                    <span className={styles.operator}>{operator}</span>
                    {value !== null && <span className={styles.value}>{value}</span>}
                </div>
            </div>
        );
    }

    function renderValue(value: unknown, rule?: QueryRuleNode) {
        if (value === undefined || value === null || value === "") {
            return <span className={styles.valueText}><i>empty</i></span>;
        }

        if (Array.isArray(value)) {
            return (
                <span className={styles.valueList}>
                    {value.map((item, index) => (
                        <span key={`${item}-${index}`} className={styles.valueEntry}>
                            <span className={styles.valueItem}>
                                {formatValue(item, rule)}
                            </span>
                            {index < value.length - 1 && <span className={styles.valueSeparator}>, </span>}
                        </span>
                    ))}
                </span>
            );
        }

        return <span className={styles.valueText}>{formatValue(value, rule)}</span>;
    }

    function describeRule(rule: QueryRuleNode): RuleDescriptor {
        if (rule?.field === "attributes") {
            return {
                kind: "attribute",
                subject: String(Array.isArray(rule.value) ? rule.value[0] : rule.value ?? "unknown attribute"),
                forceKindLabel: true,
                hideValue: isValuelessAttributeOperator(rule.operator),
                customValue: renderValue(Array.isArray(rule.value) ? rule.value[1] : undefined, rule)
            };
        }

        if (typeof rule?.field === "string" && rule.field.startsWith("aiAttributes.")) {
            return {
                kind: "AI attribute",
                subject: formatAiAttributeId(rule.field.replace("aiAttributes.", "")),
                forceKindLabel: true
            };
        }

        if (rule?.field === "source.id") {
            return {
                kind: "source",
                subject: "source",
                forceKindLabel: false,
                customValue: renderSourceValue(rule)
            };
        }

        if (rule?.field === "itemPublishedAgeDays") {
            return {
                kind: "item",
                subject: "published age in days",
                forceKindLabel: true
            };
        }

        if (rule?.field === "attribute") {
            return {
                kind: "attribute change",
                subject: "attribute",
                forceKindLabel: true
            };
        }

        if (rule?.field === "changeType") {
            return {
                kind: "attribute change",
                subject: "change type",
                forceKindLabel: true,
                customValue: renderValue(formatChangeTypeValue(rule.value), rule)
            };
        }

        if (rule?.field === "numericDifference") {
            return {
                kind: "attribute change",
                subject: "numeric difference",
                forceKindLabel: true
            };
        }

        if (rule?.field === "source.type") {
            return {
                kind: "source",
                subject: "source type",
                forceKindLabel: true
            };
        }

        if (rule?.field === "text") {
            return {
                kind: "text",
                subject: "text",
                forceKindLabel: false
            };
        }

        return {
            kind: "field",
            subject: rule?.field ?? "unknown field",
            forceKindLabel: false
        };
    }

    function renderSourceValue(rule: QueryRuleNode) {
        const ids = toArray(rule.value);

        return (
            <span className={styles.valueList}>
                {ids.map((id, index) => {
                    const source = props.sources?.find(s => s.id === id);

                    return (
                        <span key={id} className={styles.valueEntry}>
                            <span className={styles.valueItem}>
                                {source?.name
                                    ? <Link to={`/vul-sources/${source.id}`}>{source.name}</Link>
                                    : <i>Unknown ({id})</i>}
                            </span>
                            {index < ids.length - 1 && <span className={styles.valueSeparator}>, </span>}
                        </span>
                    );
                })}
            </span>
        );
    }

    function formatValue(value: unknown, rule?: QueryRuleNode) {
        if (typeof value === "string" && (rule?.operator === "jsonRegex" || rule?.operator === "not(jsonRegex)")) {
            return `"${value}"`;
        }

        return String(value);
    }

    function formatChangeTypeValue(value: unknown) {
        switch (value) {
            case "ADDED":
                return "Added";
            case "REMOVED":
                return "Removed";
            case "UPDATED":
                return "Updated";
            default:
                return value;
        }
    }

    function shouldRenderKindLabel(descriptor: RuleDescriptor) {
        if (descriptor.forceKindLabel) {
            return true;
        }

        return descriptor.kind.toLowerCase() !== descriptor.subject.toLowerCase();
    }

    function formatBranchLabel(index: number) {
        return `Branch ${index + 1}`;
    }

    function formatCombinatorLabel(combinator?: string) {
        switch (combinator) {
            case "and":
                return "AND";
            case "or":
                return "OR";
            default:
                return combinator?.toUpperCase?.() ?? String(combinator);
        }
    }

    function formatCombinatorDescription(combinator?: string) {
        switch (combinator) {
            case "and":
                return "All branches below must match.";
            case "or":
                return "At least one branch below must match.";
            default:
                return combinator;
        }
    }

    function formatOperator(operator?: string) {
        switch (operator) {
            case "jsonRegex":
                return "matches regex";
            case "not(jsonRegex)":
                return "does not match regex";
            case "jsonIcontains":
                return "contains";
            case "not(jsonIcontains)":
                return "does not contain";
            case "notIn":
                return "not in";
            case "in":
                return "in";
            case "jsonEq":
            case "eq":
            case "textEq":
                return "=";
            case "jsonGe":
            case "ge":
                return ">=";
            case "jsonLe":
            case "le":
                return "<=";
            case "gt":
                return ">";
            case "lt":
                return "<";
            case "ne":
            case "textNe":
                return "!=";
            case "textContains":
                return "contains";
            case "jsonPresent":
                return "is present";
            case "jsonMissing":
                return "is missing";
            default:
                return operator ?? "matches";
        }
    }
}

function isGroupNode(node: QueryNode): node is QueryGroupNode {
    return Array.isArray(node?.rules) && typeof node?.combinator === "string";
}

function toArray<T>(value: T | T[] | null | undefined): T[] {
    if (Array.isArray(value)) {
        return value;
    }

    if (value === undefined || value === null) {
        return [];
    }

    return [value];
}

export default ReadOnlyQueryView;
